import '../IntegrationsTab.scss'
import React, { useMemo, useState } from 'react'
import { Col, Container, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { useDeleteUserIntegrationMutation, useGetMeQuery } from '../../../../store/user/user'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { WealthboxSettingsModal } from './WealthboxSettingsModal'
import { createState, IntegrationServiceId } from '../../../../common/integrations'
import { getIntegrationRedirectUri } from '../../../Integration/utils'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'

export type WealthboxIntegrationRowProps = { useIntegrationRedirectUri?: boolean }
export const WealthboxIntegrationRow: React.FC<WealthboxIntegrationRowProps> = ({ useIntegrationRedirectUri }) => {
  const accountId = useAppSelector(selectAccountId)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()
  const hasWealthboxIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.WEALTHBOX))
  }, [userData])

  const handleShowSettingsModal = async () => {
    setShowSettingsModal(true)
  }

  const handleHideSettings = () => {
    setShowSettingsModal(false)
  }

  const authorizeWealthbox = () => {
    const redirectUri = getIntegrationRedirectUri(IntegrationServiceId.WEALTHBOX)
    const state = useIntegrationRedirectUri ? createState(window.location.origin + '/dashboard/account/profile-settings/integrations') : createState()
    window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${IntegrationServiceId.WEALTHBOX}/enable?redirect_uri=${redirectUri}&state=${state}`
  }

  const uninstallWealthbox = () => {
    const user = userData
    const integration = user!.integrations!.find((i) => i.serviceId === IntegrationServiceId.WEALTHBOX)
    deleteUserIntegration({
      accountId,
      payload: {
        userId: user!.id,
        integrationId: integration!.id,
      },
    })
  }

  return (
    <Container className='p-3 p-md-0 py-md-3 integration-container'>
      {showSettingsModal && <WealthboxSettingsModal show={showSettingsModal} onHide={handleHideSettings} />}

      <Row className='d-flex justify-content-between'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
              <IntegrationIcon serviceId={IntegrationServiceId.WEALTHBOX} width={48} height={48} />
            </div>
            <div>
              <h6>Wealthbox</h6>
              <div>Connect your Zocks account to Wealthbox.</div>
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-start'>
            {hasWealthboxIntegration && (
              <div className='d-flex gap-3'>
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'account:read', superScope: ['account:admin'] }}>
                  <Button variant='primaryGhost' onClick={handleShowSettingsModal}>
                    <i className='bi bi-gear' />
                  </Button>
                </ScopedBlock>
              </div>
            )}
            <div className='connect-button-container'>
              {hasWealthboxIntegration ? (
                <div className='connect-button-container'>
                  <Button variant='primary' onClick={uninstallWealthbox} loading={isDeletingIntegration}>
                    Disconnect
                  </Button>
                  <div className='pt-2 d-flex justify-content-end zocks-text-success'>
                    <i className='bi bi-check' />
                    Connected
                  </div>
                </div>
              ) : (
                <ScopedBlock
                  scope={{
                    scope: 'integration:connect',
                    superScope: ['integration:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button variant='primary' onClick={authorizeWealthbox}>
                    Connect
                  </Button>
                </ScopedBlock>
              )}
            </div>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
